import React from "react";
import { Badge } from "@wtc/ui/components/badge";
import { Popover, ScrollArea, Group, Stack, Text } from "@mantine/core";
import { getCurrentWeekFollowUpTag } from "@/lib/utils/crm/follow-up-tagging";

interface TagsDisplayProps {
  tags?: string[];
  maxTags?: number;
  className?: string;
}

/**
 * TagsDisplay - Renders tags as visual badges
 * Supports limiting the number of visible tags; the "+X more" indicator is a
 * real Mantine popover (portals above any modal) listing the hidden tags —
 * never a dead label.
 */
export function TagsDisplay({ tags, maxTags = 3, className = "" }: TagsDisplayProps) {
  const [opened, setOpened] = React.useState(false);

  if (!tags || tags.length === 0) {
    return null;
  }

  const visibleTags = tags.slice(0, maxTags);
  const hiddenTags = tags.slice(maxTags);
  const hiddenCount = hiddenTags.length;
  const currentWeekTag = getCurrentWeekFollowUpTag();

  // Color mapping for special tags
  const getTagColor = (tag: string) => {
    if (tag === currentWeekTag) {
      return "bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-200 dark:hover:bg-green-900/50";
    }
    if (tag.startsWith("followed-up-")) {
      return "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700";
    }
    return "bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/50";
  };

  const getTagLabel = (tag: string) => {
    if (tag === currentWeekTag) {
      return "✓ Followed Up";
    }
    if (tag.startsWith("followed-up-")) {
      const weekStr = tag.replace("followed-up-", "");
      return `✓ ${weekStr}`;
    }
    return tag;
  };

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {visibleTags.map((tag) => (
        <Badge
          key={tag}
          variant="secondary"
          className={`text-xs font-medium px-2 py-0.5 ${getTagColor(tag)}`}
        >
          {getTagLabel(tag)}
        </Badge>
      ))}
      {hiddenCount > 0 && (
        <Popover
          position="bottom"
          withArrow
          shadow="md"
          opened={opened}
          onClose={() => setOpened(false)}
        >
          <Popover.Target>
            <span
              className="inline-flex cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setOpened((o) => !o);
              }}
            >
              <Badge
                variant="outline"
                className="text-xs font-medium px-2 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                +{hiddenCount} more
              </Badge>
            </span>
          </Popover.Target>
          <Popover.Dropdown onClick={(e) => e.stopPropagation()}>
            <Stack gap="xs" style={{ maxWidth: 320 }}>
              <Text size="sm" fw={600}>
                More tags ({hiddenCount})
              </Text>
              <ScrollArea.Autosize mah={280}>
                <Group gap="xs">
                  {hiddenTags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className={`text-xs font-medium px-2 py-0.5 ${getTagColor(tag)}`}
                    >
                      {getTagLabel(tag)}
                    </Badge>
                  ))}
                </Group>
              </ScrollArea.Autosize>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      )}
    </div>
  );
}
